<template>
  <div class="editor">
    <div class="title">{{ title }}</div>
    <div v-if="editor" class="tools">
      <button class="bold" :class="{ 'is-active': editor.isActive('bold') }" @click="editor.chain().focus().toggleBold().run()">B</button>
      <button class="italic" :class="{ 'is-active': editor.isActive('italic') }" @click="editor.chain().focus().toggleItalic().run()">I</button>
      <button class="underline" :class="{ 'is-active': editor.isActive('underline') }" @click="editor.chain().focus().toggleUnderline().run()">U</button>
      <button :class="{ 'is-active': editor.isActive('bulletList') }" @click="editor.chain().focus().toggleBulletList().run()">
        <img src="https://img.icons8.com/material-rounded/48/000000/list.png" />
      </button>
      <button :class="{ 'is-active': editor.isActive('link') }" @click="setLink">
        <img src="https://img.icons8.com/material-rounded/48/000000/link--v1.png" />
      </button>
      <button :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }" @click="editor.chain().focus().setTextAlign('left').run()">
        <img src="https://img.icons8.com/material-outlined/48/000000/align-left.png" />
      </button>
      <button :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }" @click="editor.chain().focus().setTextAlign('center').run()">
        <img src="https://img.icons8.com/material-outlined/48/000000/align-center.png" />
      </button>
      <button :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }" @click="editor.chain().focus().setTextAlign('right').run()">
        <img src="https://img.icons8.com/material-outlined/48/000000/align-right.png" />
      </button>
      <button :class="{ 'is-active': editor.isActive({ textAlign: 'justify' }) }" @click="editor.chain().focus().setTextAlign('justify').run()">
        <img src="https://img.icons8.com/material-outlined/48/000000/align-justify.png" />
      </button>
    </div>
    <EditorContent :editor="editor" class="tiptap" :style="{ height: height }" />
    <div v-if="maxlengthError" class="info">
      <div class="icon-warning" />
      {{ $t('maximumCharacter') }}
    </div>
  </div>
</template>

<script>
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { Editor, EditorContent } from '@tiptap/vue-3';

export default {
  components: { EditorContent },

  props: {
    title: {
      type: String,
    },

    value: {
      type: String,
    },

    height: {
      type: String,
    },

    maxlength: {
      type: Number,
    },
  },

  data() {
    return {
      editor: null,
    };
  },

  computed: {
    maxlengthError() {
      return this.value && this.value.length >= this.maxlength;
    },
  },

  watch: {
    value(value) {
      const isSame = this.editor.getHTML() === value;

      if (isSame) {
        return;
      }

      this.editor.commands.setContent(value, false);
    },
  },

  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit,
        Underline,
        Link.configure({
          openOnClick: true,
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
      ],
      content: this.value,
      onUpdate: () => {
        this.$emit('input', this.editor.getHTML());
      },
    });
  },

  beforeUnmount() {
    this.editor.destroy();
  },

  methods: {
    setLink() {
      const previousUrl = this.editor.getAttributes('link').href;
      const url = window.prompt('URL', previousUrl);

      // cancelled
      if (url === null) {
        return;
      }

      // empty
      if (url === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run();

        return;
      }

      // update link
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.editor {
  margin-top: 40px;
}

.title {
  color: colors.$darkblue-color;
  font-weight: 500;
  font-size: 20px;
}

.tiptap {
  margin-top: 12px;
  display: flex;
  align-items: center;
  flex-grow: 1;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  border: 1px solid colors.$lightgrey-color;
  border-radius: 5px;
  padding: 30px;
  transition: all 0.3s;
  overflow-y: auto;

  :deep(.ProseMirror) {
    width: 100%;
    height: 100%;

    a {
      color: colors.$blue-color;
      text-decoration: underline;
      cursor: pointer;
      transition: 0.3s;

      &:hover {
        color: colors.$darkblue-color;
      }
    }

    code {
      font-size: 0.9rem;
      padding: 0.25em;
      border-radius: 0.25em;
      background-color: rgba(#616161, 0.1);
      color: #616161;
      box-decoration-break: clone;
    }

    &.ProseMirror-focused {
      outline: none;
    }
  }
}

.tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: auto;

  button {
    width: max-content;
    box-sizing: border-box;
    border: 1px solid colors.$lightgrey-color;
    margin: 0 6px;
    height: 36px;
    min-width: 36px;
    padding: 4px 10px;
    border-radius: 4px;
    cursor: pointer;
    transition: 0.3s;
    object-fit: contain;
    font-size: 20px;

    &:hover,
    &.is-active {
      background: colors.$lightgrey-color;
    }

    &.bold {
      font-weight: 600;
    }

    &.italic {
      font-style: italic;
    }

    &.underline {
      text-decoration: underline;
    }

    img {
      object-fit: contain;
      height: 100%;
    }
  }
}

.info {
  height: 100%;
  color: colors.$red-color;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  margin-top: 10px;
}
</style>
